import { rgba } from '../core/util';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  shape: 'circle' | 'spark' | 'ring' | 'square';
  gravity: number;
  drag: number;
  rotation: number;
  spin: number;
}

interface FloatingText {
  x: number;
  y: number;
  vy: number;
  text: string;
  color: string;
  life: number;
  maxLife: number;
  size: number;
  bold: boolean;
}

interface Tracer {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  life: number;
  maxLife: number;
  color: string;
}

/** Lightweight particle / floating text system with an object cap. */
export class Effects {
  particles: Particle[] = [];
  texts: FloatingText[] = [];
  tracers: Tracer[] = [];
  shake = 0;
  flash: { color: string; alpha: number } | null = null;
  private rand = Math.random;

  burst(x: number, y: number, color: string, count: number, speed: number, life = 0.5, size = 3, shape: Particle['shape'] = 'circle', gravity = 0): void {
    if (this.particles.length > 600) return;
    for (let i = 0; i < count; i++) {
      const a = this.rand() * Math.PI * 2;
      const s = speed * (0.4 + this.rand() * 0.8);
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life,
        maxLife: life * (0.6 + this.rand() * 0.6),
        size: size * (0.6 + this.rand() * 0.8),
        color,
        shape,
        gravity,
        drag: 0.9,
        rotation: this.rand() * Math.PI,
        spin: (this.rand() - 0.5) * 10,
      });
    }
  }

  ring(x: number, y: number, color: string, radius: number, life = 0.4): void {
    this.particles.push({
      x,
      y,
      vx: 0,
      vy: 0,
      life,
      maxLife: life,
      size: radius,
      color,
      shape: 'ring',
      gravity: 0,
      drag: 1,
      rotation: 0,
      spin: 0,
    });
  }

  text(x: number, y: number, text: string, color: string, size = 14, bold = true, life = 0.9): void {
    if (this.texts.length > 40) this.texts.shift();
    this.texts.push({ x: x + (this.rand() - 0.5) * 10, y, vy: -38, text, color, life, maxLife: life, size, bold });
  }

  tracer(x1: number, y1: number, x2: number, y2: number, color: string, life = 0.25): void {
    this.tracers.push({ x1, y1, x2, y2, life, maxLife: life, color });
  }

  addShake(amount: number): void {
    this.shake = Math.min(14, this.shake + amount);
  }

  addFlash(color: string, alpha: number): void {
    this.flash = { color, alpha };
  }

  update(dt: number): void {
    for (const p of this.particles) {
      p.life -= dt;
      p.vy += p.gravity * dt;
      p.vx *= Math.pow(p.drag, dt * 60);
      p.vy *= Math.pow(p.drag, dt * 60);
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rotation += p.spin * dt;
    }
    this.particles = this.particles.filter((p) => p.life > 0);
    for (const t of this.texts) {
      t.life -= dt;
      t.y += t.vy * dt;
      t.vy *= 0.96;
    }
    this.texts = this.texts.filter((t) => t.life > 0);
    for (const t of this.tracers) t.life -= dt;
    this.tracers = this.tracers.filter((t) => t.life > 0);
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 30);
    if (this.flash) {
      this.flash.alpha -= dt * 1.6;
      if (this.flash.alpha <= 0) this.flash = null;
    }
  }

  drawParticles(ctx: CanvasRenderingContext2D): void {
    for (const p of this.particles) {
      const k = Math.max(0, p.life / p.maxLife);
      ctx.globalAlpha = Math.min(1, k * 1.4);
      switch (p.shape) {
        case 'circle':
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * (0.5 + k * 0.5), 0, Math.PI * 2);
          ctx.fill();
          break;
        case 'square':
          ctx.fillStyle = p.color;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
          ctx.restore();
          break;
        case 'spark': {
          ctx.strokeStyle = p.color;
          ctx.lineWidth = Math.max(1, p.size * 0.5);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - p.vx * 0.04, p.y - p.vy * 0.04);
          ctx.stroke();
          break;
        }
        case 'ring': {
          const r = p.size * (1 - k * 0.6);
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 2 + k * 3;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
      }
    }
    ctx.globalAlpha = 1;
  }

  drawTracers(ctx: CanvasRenderingContext2D): void {
    for (const t of this.tracers) {
      const k = t.life / t.maxLife;
      ctx.strokeStyle = rgba(t.color, k * 0.8);
      ctx.lineWidth = 2 + k * 2;
      ctx.setLineDash([6, 6]);
      ctx.lineDashOffset = -k * 40;
      ctx.beginPath();
      ctx.moveTo(t.x1, t.y1);
      ctx.lineTo(t.x2, t.y2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  drawTexts(ctx: CanvasRenderingContext2D, mirror = false): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const t of this.texts) {
      const k = t.life / t.maxLife;
      ctx.save();
      if (mirror) {
        ctx.translate(t.x, t.y);
        ctx.scale(1, -1);
        ctx.translate(-t.x, -t.y);
      }
      ctx.globalAlpha = Math.min(1, k * 2);
      ctx.font = `${t.bold ? 800 : 600} ${t.size}px Montserrat, sans-serif`;
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.strokeText(t.text, t.x, t.y);
      ctx.fillStyle = t.color;
      ctx.fillText(t.text, t.x, t.y);
      ctx.restore();
    }
  }
}
